import { AllComponents } from "../components/_default/interfaces/base";

// Updates object key value by object "id" value.
export let updateObjValueById =
  (id: string, key: string, value: any) => (obj: any) => {
    if (obj.id === ~~id) {
      obj[key].push(value);
      return true;
    } else if (obj.childElements) {
      return obj.childElements.some(updateObjValueById(id, key, value));
    }
  };

// Returns path as string or as an array.
export function getObjectPathByKeyValue(
  object: any,
  key: string,
  value: any,
  asString = true
) {
  let path: string[] = [];
  getObjPath(object, key, value);

  function getObjPath(_obj: any, _key: string, _val: any): any {
    if (!_obj || (typeof _obj !== "object" && !Array.isArray(_obj))) {
      return false;
    } else if (_obj.hasOwnProperty(_key) && _obj[_key] === _val) {
      return true;
    } else if (Array.isArray(_obj)) {
      let _parentKey = "";

      for (let i = 0; i < _obj.length; i++) {
        path.push(`${_parentKey}[${i}]`);
        const _subObj = getObjPath(_obj[i], _key, _val);
        if (_subObj) {
          return _subObj;
        }
        path.pop();
      }
    } else {
      for (const _k in _obj) {
        path.push(_k);
        const _subObj = getObjPath(_obj[_k], _key, _val);
        if (_subObj) {
          return _subObj;
        }
        path.pop();
      }
    }
    return false;
  }

  return asString
    ? path.join(".")
    : path.map((item) => item.replace("[", "").replace("]", ""));
}

// Returns path as string or as an array.
export function getObjectPathsByKeyValue(
  object: any,
  key: string,
  value: any,
  asString = true
) {
  var paths: any = [];
  getObjPaths(object, key, value, []);

  function getObjPaths(_obj: any, _key: string, _val: any, _path: any): any {
    if (!_obj || (typeof _obj !== "object" && !Array.isArray(_obj))) {
      return false;
    } else if (_obj.hasOwnProperty(_key) && _obj[_key] === _val) {
      paths.push([..._path]);
      return true;
    } else if (Array.isArray(_obj)) {
      for (let i = 0; i < _obj.length; i++) {
        _path.push(`${i}`);
        _path.pop();
      }
    } else {
      for (const _k in _obj) {
        _path.push(_k);
        _path.pop();
      }
    }
    return false;
  }

  return asString ? paths.join(".") : paths;
}

export function findComponentById(
  structure: any,
  id: number
): AllComponents | undefined {
  return structure.components.find((c: { id: number }) => c.id === id);
}

// Cuz col and row are different but have same type
export function getComponentType(component: any) {
  if (!component || typeof component !== "object") {
    return "col";
  }

  if (component.type === "section" && component.attributes?.classes) {
    if (component.attributes.classes.includes("row")) {
      return "row";
    } else {
      return "col";
    }
  }
  return component.type;
}

/**
 * Default Bootstrap grid classes for new column (`section` with `col`).
 * Matches ColMenu breakpoints: full width XS–MD, one third on LG–XL.
 */
export const DEFAULT_NEW_COLUMN_CLASSES =
  "col col-12 col-sm-12 col-md-12 col-lg-4 col-xl-4";

// 'element', 'row' or 'col'
export function getTemplateCompType(component: any) {
  let comp = getComponentType(component);
  if (!["row", "col"].includes(comp)) {
    return "element";
  }
  return comp;
}

export function inClass(
  className: string,
  el: HTMLElement | null | undefined
): HTMLElement | null {
  if (!el) {
    return null;
  }
  if (el.classList?.contains(className)) {
    return el;
  }
  if (el.parentElement) {
    return inClass(className, el.parentElement);
  }
  return null;
}

export function setComponentPropertyRecursively(
  component: any,
  property: string,
  value: any
) {
  component[property] = value;
  component.childElements.forEach((comp: any) => {
    setComponentPropertyRecursively(comp, property, value);
  });
  return component;
}

export function dateDiffInDays(f_date: number, s_date: number) {
  return Math.abs(Math.round((s_date - f_date) / (1000 * 60 * 60 * 24)));
}

// Page selection persistence utilities
// Keeps track of the selected page across browser refreshes with time-based expiry
const ACTIVE_PAGE_KEY = "builder_active_page";
const DEFAULT_EXPIRY_HOURS = 24; // 24 hours default expiry

/** Legacy shape (numeric webpage id). */
interface StoredPageSelectionV1 {
  pageId: number;
  timestamp: number;
  expiryTime: number;
}

/** Current shape (webpage UUID from builder V2). */
interface StoredPageSelectionV2 {
  webpageUuid: string;
  timestamp: number;
  expiryTime: number;
}

/**
 * Saves the active page selection to localStorage with expiration time
 * @param webpageUuid - Stable webpage UUID (builder V2)
 * @param expiryHours - Hours after which the selection expires (default: 24)
 */

export function saveActivePageSelection(
  webpageUuid: string,
  expiryHours: number = DEFAULT_EXPIRY_HOURS
): void {
  if (!isLocalStorageAvailable()) {
    return;
  }

  if (!webpageUuid || typeof webpageUuid !== "string") {
    console.warn("Invalid webpageUuid provided for saving:", webpageUuid);
    return;
  }

  try {
    const now = Date.now();
    const expiryTime = now + expiryHours * 60 * 60 * 1000; // Convert hours to milliseconds

    const storageData: StoredPageSelectionV2 = {
      webpageUuid,
      timestamp: now,
      expiryTime,
    };

    localStorage.setItem(ACTIVE_PAGE_KEY, JSON.stringify(storageData));
  } catch (error) {
    console.warn(
      "Failed to save active page selection to localStorage:",
      error
    );
  }
}

/**
 * Retrieves the stored active page selection from localStorage
 * @returns Webpage UUID, or legacy numeric page id, or null
 */
export function getStoredActivePageSelection(): string | number | null {
  if (!isLocalStorageAvailable()) {
    return null;
  }

  try {
    const storedData = localStorage.getItem(ACTIVE_PAGE_KEY);
    if (!storedData) {
      return null;
    }

    const parsedData = JSON.parse(storedData) as
      | StoredPageSelectionV1
      | StoredPageSelectionV2;
    const now = Date.now();

    if (!parsedData.expiryTime) {
      console.warn("Invalid stored page selection data, clearing storage");
      localStorage.removeItem(ACTIVE_PAGE_KEY);
      return null;
    }

    if (now > parsedData.expiryTime) {
      localStorage.removeItem(ACTIVE_PAGE_KEY);
      return null;
    }

    if (
      "webpageUuid" in parsedData &&
      typeof parsedData.webpageUuid === "string" &&
      parsedData.webpageUuid !== ""
    ) {
      return parsedData.webpageUuid;
    }

    if (
      "pageId" in parsedData &&
      typeof parsedData.pageId === "number" &&
      parsedData.pageId > 0
    ) {
      return parsedData.pageId;
    }

    console.warn("Invalid stored page selection data, clearing storage");
    localStorage.removeItem(ACTIVE_PAGE_KEY);
    return null;
  } catch (error) {
    console.warn(
      "Failed to retrieve active page selection from localStorage:",
      error
    );
    try {
      localStorage.removeItem(ACTIVE_PAGE_KEY);
    } catch (cleanupError) {
      // Ignore cleanup errors
    }
    return null;
  }
}

/**
 * Clears the stored active page selection from localStorage
 */
export function clearStoredActivePageSelection(): void {
  try {
    localStorage.removeItem(ACTIVE_PAGE_KEY);
  } catch (error) {
    console.warn(
      "Failed to clear active page selection from localStorage:",
      error
    );
  }
}

/**
 * Validates that a stored page reference exists in the available pages list
 * @param stored - Webpage UUID (string) or legacy numeric id
 * @param availablePages - Array of available pages to check against
 */
export function validateStoredPageExists(
  stored: string | number | null | undefined,
  availablePages: any[]
): boolean {
  if (
    stored == null ||
    stored === "" ||
    !Array.isArray(availablePages) ||
    availablePages.length === 0
  ) {
    return false;
  }
  if (typeof stored === "string") {
    return availablePages.some((page) => page && page.uuid === stored);
  }
  if (typeof stored === "number") {
    return availablePages.some((page) => page && page.id === stored);
  }
  return false;
}

/** Map legacy stored numeric id to webpage UUID once pages are loaded. */
export function resolveStoredSelectionToWebpageUuid(
  stored: string | number | null,
  availablePages: { id?: number; uuid?: string }[]
): string | null {
  if (stored == null) {
    return null;
  }
  if (typeof stored === "string") {
    return stored;
  }
  const page = availablePages.find((p) => p && p.id === stored);
  return page?.uuid && page.uuid !== "" ? page.uuid : null;
}

// Additional utility to check if localStorage is available
export function isLocalStorageAvailable(): boolean {
  try {
    const test = "__localStorage_test__";
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (error) {
    return false;
  }
}

// Utility to get storage info for debugging
export function getStorageInfo(): {
  hasStorage: boolean;
  storedPageId: number | null;
  storedWebpageUuid: string | null;
  isExpired: boolean;
} {
  if (!isLocalStorageAvailable()) {
    return {
      hasStorage: false,
      storedPageId: null,
      storedWebpageUuid: null,
      isExpired: false,
    };
  }

  try {
    const storedData = localStorage.getItem(ACTIVE_PAGE_KEY);
    if (!storedData) {
      return {
        hasStorage: true,
        storedPageId: null,
        storedWebpageUuid: null,
        isExpired: false,
      };
    }

    const parsedData = JSON.parse(storedData) as
      | StoredPageSelectionV1
      | StoredPageSelectionV2;
    const now = Date.now();
    const isExpired = now > parsedData.expiryTime;

    const storedWebpageUuid =
      "webpageUuid" in parsedData &&
      typeof parsedData.webpageUuid === "string"
        ? parsedData.webpageUuid
        : null;
    const storedPageId =
      "pageId" in parsedData && typeof parsedData.pageId === "number"
        ? parsedData.pageId
        : null;

    return {
      hasStorage: true,
      storedPageId,
      storedWebpageUuid,
      isExpired,
    };
  } catch (error) {
    return {
      hasStorage: true,
      storedPageId: null,
      storedWebpageUuid: null,
      isExpired: false,
    };
  }
}
